import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { User, Settings, Clock, Check, Loader2, Plus, X, Edit3, Trash2, Save, Volume2, VolumeX, RefreshCw, Package, Lock, Monitor, Eye, Printer, CheckCircle, XCircle, ToggleLeft, ToggleRight } from "lucide-react";

const isElectron = !!(window.electronAPI?.isElectron);

const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port, pr = window.location.protocol;
  if (pr === "file:") return "https://efis.pro/api";
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  if (h === "localhost" && !p) return "http://localhost/fiscalization/api";
  return "https://efis.pro/api";
})();

async function api(path, opts = {}) {
  const token = localStorage.getItem("kitchen_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: h, ...opts });
    if (r.status === 401) { localStorage.removeItem("kitchen_token"); localStorage.removeItem("kitchen_user"); window.location.reload(); return { success: false }; }
    return await r.json();
  } catch (e) { return { success: false, message: "Network error" }; }
}

// PIN system
const PIN_KEY = "kitchen_pin_system";
const PIN_ENC = "efis_kitchen_2026_kds";
function pe(t) { let r = ""; for (let i = 0; i < t.length; i++) r += String.fromCharCode(t.charCodeAt(i) ^ PIN_ENC.charCodeAt(i % PIN_ENC.length)); return btoa(r); }
function pd(e) { try { const t = atob(e); let r = ""; for (let i = 0; i < t.length; i++) r += String.fromCharCode(t.charCodeAt(i) ^ PIN_ENC.charCodeAt(i % PIN_ENC.length)); return r; } catch { return null; } }
async function hashPin(p) { const d = new TextEncoder().encode(p + "_efis_kitchen_salt"); const h = await crypto.subtle.digest("SHA-256", d); return Array.from(new Uint8Array(h)).map(b => b.toString(16).padStart(2, "0")).join(""); }
function loadPinData() { try { const r = localStorage.getItem(PIN_KEY); if (!r) return null; const d = pd(r); return d ? JSON.parse(d) : null; } catch { return null; } }
function savePinData(d) { try { localStorage.setItem(PIN_KEY, pe(JSON.stringify(d))); } catch {} }

// Auto-gotovo settings per sector: { sectorId: { enabled: bool, minutes: int } }
function loadAutoGotovo() { try { return JSON.parse(localStorage.getItem("kitchen_auto_gotovo") || "{}"); } catch { return {}; } }
function saveAutoGotovo(d) { localStorage.setItem("kitchen_auto_gotovo", JSON.stringify(d)); }

// ═══ ALERT SOUND (Web Audio) ═══
// Smart-TV / kiosk browsers block autoplay until a user gesture. An <audio>
// element with a data-URI is unreliable there. We instead synthesize the beep
// with the Web Audio API and explicitly RESUME the AudioContext on the very
// first user gesture anywhere in the app (login tap, PIN entry, any key/touch).
// By the time the cook is looking at the KDS, audio is unlocked, so every
// later "new order" beep plays with no further interaction needed.
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) { try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { _audioCtx = null; } }
  return _audioCtx;
}
function unlockAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try { const o = ctx.createOscillator(), g = ctx.createGain(); g.gain.value = 0.0001; o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.01); } catch {}
}
if (typeof window !== "undefined") {
  const onGesture = () => unlockAudio();
  ["pointerdown", "touchstart", "keydown", "click"].forEach(ev => window.addEventListener(ev, onGesture, { passive: true }));
}
// Loud, repeating square-wave beep — piercing enough to carry across a noisy
// kitchen. `beeps` controls how aggressive it is.
function playAlert(beeps = 4) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const now = ctx.currentTime, dur = 0.18, gap = 0.1;
  for (let i = 0; i < beeps; i++) {
    const t = now + i * (dur + gap);
    const osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(i % 2 === 0 ? 940 : 1200, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.55, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t); osc.stop(t + dur + 0.02);
  }
}

// ═══ DYNAMIC GRID + CARD METRICS ═══
// computeGrid: pick the column/row split that tiles N cards into a fixed WxH
// area with cells closest to a pleasant aspect ratio (no scrolling). This is
// what lets 20/30/40 orders all fit at once — cards shrink as the count grows.
function computeGrid(n, W, H, gap) {
  if (!n || n < 1) return { cols: 1, rows: 1 };
  W = Math.max(1, W); H = Math.max(1, H);
  const target = 0.8; // desired card width/height ratio (slightly taller than wide)
  let best = { cols: 1, rows: n, score: -Infinity };
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const cw = (W - gap * (cols - 1)) / cols;
    const ch = (H - gap * (rows - 1)) / rows;
    if (cw <= 20 || ch <= 20) continue;
    const ar = cw / ch;
    const aspectScore = -Math.abs(Math.log(ar / target));
    const sizeScore = Math.log(Math.min(cw, ch));
    const score = aspectScore * 1.6 + sizeScore;
    if (score > best.score) best = { cols, rows, score };
  }
  return best;
}
// cardMetrics: given a card's available height and how many products it holds,
// derive every font/padding/button size so ALL products fit inside the card.
// Fewer items -> bigger text; an order with 13 products -> smaller text, but
// still all visible. Everything is clamped to stay legible.
function cardMetrics(cardH, nItems) {
  const n = Math.max(1, nItems || 1);
  const h = Math.max(80, cardH || 200);
  const headFont = Math.max(11, Math.min(20, h * 0.075));
  const footFont = Math.max(11, Math.min(18, h * 0.07));
  const headH = headFont * 2.4;
  const footH = footFont * 2.6;
  const itemsH = Math.max(28, h - headH - footH - 6);
  const per = itemsH / n;                       // vertical slot available per product
  const itemFont = Math.max(8, Math.min(28, per * 0.46));
  const itemPad = Math.max(1, Math.min(9, per * 0.13));
  const btn = Math.max(22, Math.min(46, per * 0.66));
  const tagFont = Math.max(8, Math.min(13, itemFont * 0.62));
  const gap = Math.max(4, itemFont * 0.4);
  const headPad = Math.max(5, Math.min(14, h * 0.035));
  const footPad = Math.max(4, Math.min(12, h * 0.03));
  const btnPad = Math.max(6, Math.min(14, footFont * 0.7));
  return { headFont, footFont, itemFont, itemPad, btn, tagFont, gap, headPad, footPad, btnPad };
}

// ═══ SELF DISPLAY CONFIG ═══
// Per-machine localStorage config for the SELF Display view (a dedicated
// kitchen view showing only orders with source='self'). When enabled, the
// admin sets a PIN to open it from the PIN screen, and can choose which
// sectors / products to exclude from the SELF Display view (useful when
// only certain products should appear on the SELF kitchen station).
//
// Config shape:
//   {
//     enabled: false,                     // master ON/OFF
//     pin_hash: null,                     // SHA-256 hash of 4-digit PIN
//     excluded_sectors: [],               // sector IDs to HIDE on SELF Display
//     excluded_products: []               // product IDs to HIDE on SELF Display
//   }
//
// Per-machine by design — every kitchen tablet/PC has its own config so
// different displays can be set up differently in the same restaurant.
const SELF_DISPLAY_KEY = "kitchen_self_display_config";
function loadSelfDisplayConfig() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SELF_DISPLAY_KEY) || "{}");
    return {
      enabled: !!parsed.enabled,
      pin_hash: parsed.pin_hash || null,
      excluded_sectors: Array.isArray(parsed.excluded_sectors) ? parsed.excluded_sectors : [],
      excluded_products: Array.isArray(parsed.excluded_products) ? parsed.excluded_products : [],
    };
  } catch {
    return { enabled: false, pin_hash: null, excluded_sectors: [], excluded_products: [] };
  }
}
function saveSelfDisplayConfig(c) {
  try { localStorage.setItem(SELF_DISPLAY_KEY, JSON.stringify(c)); } catch {}
}

// ── Smart polling config (v1.7) ─────────────────────────────────────────────
// Base cadence when the screen is visible and the kitchen is busy.
const POLL = 7000;              // active interval (was 5000). 7s per request.
// When the tab/window is hidden (TV screensaver, switched app, PC asleep) we
// stop polling entirely and resume on focus — kills all idle-hours traffic.
// When a request fails (e.g. a 403 burst from the host firewall) we back off
// exponentially instead of hammering, so one block can't snowball into an outage.
const POLL_MAX = 60000;         // max backoff interval on repeated failures (60s)
const POLL_JITTER = 2000;       // up to +2s random per tick so multiple screens
                                // / sectors never fire on the exact same instant.
const C = { bg: "#0f0f0f", surface: "#1a1a1a", card: "#222", border: "#333", text: "#e5e5e5", muted: "#777", accent: "#f59e0b", green: "#22c55e", red: "#ef4444", blue: "#3b82f6", font: "'JetBrains Mono','DM Sans',monospace" };
const inp = { width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, color: C.text, fontSize: 14, fontFamily: C.font, boxSizing: "border-box", outline: "none" };

// ═══ THERMAL PRINT ═══
function getKitchenPrinterSettings() {
  return {
    type: localStorage.getItem("kitchen_printer_type") || (isElectron ? "usb" : "system"),
    addr: localStorage.getItem("kitchen_printer_addr") || "",
    width: localStorage.getItem("kitchen_paper_width") || "80"
  };
}

function buildTicketHTML(order, sectorName, allSectors, paperWidth) {
  const now = new Date();
  const time = now.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" });
  const w = parseInt(paperWidth) || 80;
  let html = `<div style="font-family:monospace;font-size:${w <= 58 ? 11 : 13}px;width:${w - 10}mm;padding:4px">`;
  html += `<div style="text-align:center;font-size:${w <= 58 ? 14 : 18}px;font-weight:bold;border-bottom:2px dashed #000;padding-bottom:6px;margin-bottom:8px">`;
  html += `🍳 ${sectorName || "KUHINJA"}</div>`;
  html += `<div style="font-size:${w <= 58 ? 13 : 16}px;font-weight:bold">Narudžba #${order.order_number || order.id}</div>`;
  if (order.table_name) html += `<div style="font-size:${w <= 58 ? 12 : 14}px;margin-top:2px">📍 ${order.table_name}</div>`;
  html += `<div style="font-size:${w <= 58 ? 10 : 12}px;color:#666;margin-bottom:8px">${time}</div>`;
  html += `<div style="border-top:1px dashed #000;padding-top:6px">`;
  (order.items || []).forEach(it => {
    const secLabel = allSectors && it.sector_id ? (allSectors.find(s => s.id == it.sector_id)?.name || "") : "";
    html += `<div style="padding:4px 0;border-bottom:1px dotted #ccc">`;
    html += `<div style="font-size:${w <= 58 ? 13 : 15}px;font-weight:bold">${it.quantity}× ${it.product_name}</div>`;
    if (secLabel) html += `<div style="font-size:11px;color:#888">[${secLabel}]</div>`;
    if ((it.tags || []).length > 0) html += `<div style="font-size:${w <= 58 ? 10 : 12}px;color:#b45309;margin-top:2px">🏷 ${it.tags.join(", ")}</div>`;
    html += `</div>`;
  });
  html += `</div><div style="text-align:center;margin-top:10px;border-top:2px dashed #000;padding-top:6px;font-size:10px">EFIS.PRO Kitchen</div></div>`;
  return html;
}

function buildTicketText(order, sectorName, allSectors, paperWidth, companyName) {
  const w = parseInt(paperWidth) <= 58 ? 32 : 48;
  const sep = "=".repeat(w);
  const dash = "-".repeat(w);
  const now = new Date();
  const time = now.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString("sr-Latn-ME", { day: "2-digit", month: "2-digit", year: "numeric" });
  let txt = "";
  // ESC/POS init
  txt += "\x1B\x40"; // Reset printer
  txt += "\n";
  // Company name — centered, bold, double height
  txt += "\x1B\x61\x01"; // Center
  txt += "\x1B\x45\x01"; // Bold ON
  txt += "\x1D\x21\x01"; // Double height
  txt += (companyName || "KUHINJA").toUpperCase() + "\n";
  txt += "\x1D\x21\x00"; // Normal size
  txt += "\x1B\x45\x00"; // Bold OFF
  txt += sep + "\n";
  // Sector name — centered, bold, double size
  txt += "\x1B\x45\x01"; // Bold ON
  txt += "\x1D\x21\x11"; // Double width + double height
  txt += (sectorName || "KUHINJA").substring(0, Math.floor(w / 2) - 1) + "\n";
  txt += "\x1D\x21\x00"; // Normal
  txt += "\x1B\x45\x00"; // Bold OFF
  txt += sep + "\n";
  // Order number — bold, double height
  txt += "\x1B\x45\x01";
  txt += "\x1D\x21\x01"; // Double height
  txt += "#" + (order.order_number || order.id) + "\n";
  txt += "\x1D\x21\x00";
  txt += "\x1B\x45\x00";
  // Table name — double height if present
  if (order.table_name) {
    txt += "\x1D\x21\x01";
    txt += "Sto: " + order.table_name + "\n";
    txt += "\x1D\x21\x00";
  }
  // Date and time — bold
  txt += "\x1B\x45\x01";
  txt += date + "  " + time + "\n";
  txt += "\x1B\x45\x00";
  txt += "\x1B\x61\x00"; // Left align
  txt += dash + "\n";
  // Items — each item bold + double height, tags on separate lines
  (order.items || []).forEach(it => {
    const secLabel = allSectors && it.sector_id ? (allSectors.find(s => s.id == it.sector_id)?.name || "") : "";
    // Product name — bold, double height
    txt += "\x1B\x45\x01";
    txt += "\x1D\x21\x01"; // Double height
    txt += it.quantity + "x " + it.product_name + "\n";
    txt += "\x1D\x21\x00";
    txt += "\x1B\x45\x00";
    // Sector label
    if (secLabel) {
      txt += "  [" + secLabel + "]\n";
    }
    // Tags — each on its own line, bold for visibility
    if ((it.tags || []).length > 0) {
      txt += "\x1B\x45\x01";
      it.tags.forEach(tag => {
        txt += "  >> " + tag + "\n";
      });
      txt += "\x1B\x45\x00";
    }
    txt += "\n";
  });
  txt += dash + "\n";
  txt += "\x1B\x61\x01"; // Center
  txt += "EFIS.PRO Kitchen\n";
  txt += sep + "\n";
  // Feed and cut
  txt += "\n\n\x1D\x56\x41\x03";
  return txt;
}

// Print queue to ensure sequential printing (POS receipt first, then kitchen)
let kitchenPrintQueue = [];
let kitchenPrintBusy = false;

async function processKitchenPrintQueue() {
  if (kitchenPrintBusy || kitchenPrintQueue.length === 0) return;
  kitchenPrintBusy = true;
  const job = kitchenPrintQueue.shift();
  let attempts = 0;
  const maxAttempts = 3;
  while (attempts < maxAttempts) {
    try {
      const result = await job.fn();
      if (result?.success !== false) break;
    } catch (e) { console.error("Print attempt failed:", e); }
    attempts++;
    if (attempts < maxAttempts) await new Promise(r => setTimeout(r, 1500)); // Wait 1.5s between retries
  }
  kitchenPrintBusy = false;
  setTimeout(processKitchenPrintQueue, 300); // Small delay before next job
}

async function printKitchenTicket(order, sectorName, allSectors, companyName) {
  const ps = getKitchenPrinterSettings();

  const doPrint = async () => {
    if (isElectron && window.electronAPI) {
      const txt = buildTicketText(order, sectorName, allSectors, ps.width, companyName);
      return await window.electronAPI.printText(txt);
    }

    if (ps.type === "network" && ps.addr) {
      try {
        const txt = buildTicketText(order, sectorName, allSectors, ps.width, companyName);
        const printUrl = ps.addr.endsWith("/") ? ps.addr.slice(0, -1) + "/print/text" : ps.addr + "/print/text";
        const r = await fetch(printUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: txt }) });
        return await r.json();
      } catch (e) { console.error("Network print error:", e); return { success: false }; }
    }

    // System dialog (browser) — full page receipt
    const html = buildTicketHTML(order, sectorName, allSectors, ps.width);
    const w = parseInt(ps.width) || 80;
    const win = window.open("", "_blank", "width=350,height=700");
    if (win) {
      win.document.write(`<html><head><style>@page{size:${w}mm auto;margin:0mm 2mm;}*{margin:0;padding:0;box-sizing:border-box;}body{width:${w - 6}mm;}</style></head><body>${html}<script>setTimeout(()=>{window.print();setTimeout(()=>window.close(),1000)},500)<\/script></body></html>`);
      win.document.close();
    }
    return { success: true };
  };

  // Queue the print job
  kitchenPrintQueue.push({ fn: doPrint });
  processKitchenPrintQueue();
}

// ═══ HELPERS ═══
// Robust timestamp parser. Handles "YYYY-MM-DD HH:MM:SS", ISO, and odd inputs.
// Returns NaN for missing/invalid values instead of throwing.
function parseTs(v) {
  if (!v) return NaN;
  if (typeof v === "number") return v;
  let t = new Date(String(v).replace(" ", "T")).getTime();
  if (isNaN(t)) t = new Date(v).getTime();
  return t;
}

function OrderTimer({ createdAt }) {
  const [e, setE] = useState(0);
  const seenAt = useRef(Date.now()); // client clock anchor: when THIS screen first saw the order
  useEffect(() => {
    const startMs = parseTs(createdAt);
    const tick = () => {
      const now = Date.now();
      let secs = isNaN(startMs) ? NaN : Math.floor((now - startMs) / 1000);
      // If the device clock makes the elapsed impossible (negative, e.g. a TV
      // whose clock is behind the server) or unparseable, fall back to counting
      // from when this screen first saw the order — so the timer NEVER freezes
      // at 0:00. When the device clock is correct, true age from created_at is used.
      if (isNaN(secs) || secs < 0) secs = Math.floor((now - seenAt.current) / 1000);
      setE(Math.max(0, secs));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [createdAt]);
  const m = Math.floor(e / 60), s = e % 60;
  return <span style={{ fontFamily: C.font, fontWeight: 700, fontSize: 15, color: m >= 10 ? C.red : m >= 5 ? C.accent : C.green, display: "flex", alignItems: "center", gap: 4, flexShrink: 0, whiteSpace: "nowrap" }}><Clock size={14} />{m}:{s.toString().padStart(2, "0")}</span>;
}
function LiveClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setT(new Date()), 1000); return () => clearInterval(iv); }, []);
  return <span style={{ fontFamily: C.font, fontWeight: 600 }}>{t.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>;
}

// ═══ LOGIN ═══
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [loading, setL] = useState(false); const [err, setErr] = useState("");
  const sub = async e => { e.preventDefault(); setL(true); setErr(""); const d = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password: pw, app: "pos" }) }); setL(false); d.success ? onLogin(d.data.token, d.data.operator) : setErr(d.message || "Login failed"); };
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: C.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ width: 400, background: C.surface, borderRadius: 16, padding: "40px 32px", border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}><div style={{ fontSize: 36, marginBottom: 8 }}>🍳</div><h1 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>EFIS.PRO <span style={{ color: C.accent }}>Kitchen</span></h1><p style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>Kuhinjski displej sistem</p></div>
        <form onSubmit={sub}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={{ ...inp, marginBottom: 14 }} />
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Lozinka</label>
          <input type="password" value={pw} onChange={e => setPw(e.target.value)} style={{ ...inp, marginBottom: 18 }} />
          {err && <div style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${C.red}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, color: C.red, fontSize: 13 }}>{err}</div>}
          <button type="submit" disabled={loading} style={{ width: "100%", padding: 14, background: C.accent, border: "none", borderRadius: 8, color: "#000", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: C.font }}>{loading ? "..." : "Prijavi se"}</button>
        </form>
      </div>
    </div>
  );
}

// ═══ PIN SCREEN ═══
function PinScreen({ onUnlock, pinData, companyName }) {
  const [pin, setPin] = useState(""); const [err, setErr] = useState(""); const [shake, setShake] = useState(false); const [chk, setChk] = useState(false);
  const [t, setT] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setT(new Date()), 1000); return () => clearInterval(iv); }, []);
  const digit = async d => {
    if (chk) return; const n = pin + d; setPin(n); setErr("");
    if (n.length === 4) {
      setChk(true); const h = await hashPin(n);
      if (pinData.admin_pin_hash === h) { onUnlock({ type: "admin" }); setChk(false); return; }
      const sec = (pinData.sector_pins || []).find(s => s.pin_hash === h);
      if (sec) { onUnlock({ type: "sector", sector_id: sec.sector_id, sector_name: sec.sector_name }); setChk(false); return; }
      // SELF Display PIN (only matches when SELF Display is enabled in config)
      const selfCfg = loadSelfDisplayConfig();
      if (selfCfg.enabled && selfCfg.pin_hash && selfCfg.pin_hash === h) {
        onUnlock({ type: "self_display" }); setChk(false); return;
      }
      setShake(true); setErr("Pogrešan PIN"); setTimeout(() => { setPin(""); setShake(false); setChk(false); }, 600);
    }
  };
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: C.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <style>{`@keyframes sk{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-12px)}40%,80%{transform:translateX(12px)}}@keyframes dp{0%{transform:scale(0)}50%{transform:scale(1.3)}100%{transform:scale(1)}}`}</style>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🍳</div>
      <div style={{ textAlign: "center", marginBottom: 24 }}><div style={{ color: C.text, fontSize: 36, fontWeight: 200, letterSpacing: 3 }}>{t.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" })}</div><div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{t.toLocaleDateString("sr-Latn-ME", { weekday: "long", day: "numeric", month: "long" })}</div></div>
      <div style={{ color: C.muted, fontSize: 13, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Unesite PIN</div>
      <div style={{ display: "flex", gap: 14, marginBottom: 8, animation: shake ? "sk 0.5s ease" : "none" }}>{[0, 1, 2, 3].map(i => <div key={i} style={{ width: 16, height: 16, borderRadius: "50%", border: pin.length > i ? "none" : `2px solid ${C.border}`, background: pin.length > i ? (err ? C.red : C.accent) : "transparent", transition: "all 0.15s", animation: pin.length === i + 1 && !err ? "dp 0.2s" : "none", boxShadow: pin.length > i ? `0 0 10px ${err ? "rgba(239,68,68,0.3)" : "rgba(245,158,11,0.3)"}` : "" }} />)}</div>
      <div style={{ height: 22, marginBottom: 12 }}>{err && <span style={{ color: C.red, fontSize: 13 }}>{err}</span>}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,68px)", gap: 10 }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <button key={n} onClick={() => digit(String(n))} style={{ width: 68, height: 68, borderRadius: 18, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 24, fontWeight: 500, cursor: "pointer", fontFamily: C.font }} onMouseDown={e => e.currentTarget.style.background = "rgba(245,158,11,0.15)"} onMouseUp={e => e.currentTarget.style.background = C.surface} onMouseLeave={e => e.currentTarget.style.background = C.surface}>{n}</button>)}
        <div /><button onClick={() => digit("0")} style={{ width: 68, height: 68, borderRadius: 18, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 24, fontWeight: 500, cursor: "pointer", fontFamily: C.font }}>0</button>
        <button onClick={() => { if (!chk) { setPin(p => p.slice(0, -1)); setErr(""); } }} style={{ width: 68, height: 68, borderRadius: 18, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 18, cursor: "pointer" }}>⌫</button>
      </div>
      <div style={{ position: "absolute", bottom: 20, color: "rgba(255,255,255,0.1)", fontSize: 12 }}>{companyName || "EFIS.PRO Kitchen"}</div>
    </div>
  );
}

// ═══ PIN SETUP ═══
function PinSetup({ user, onComplete }) {
  const [pin, setPin] = useState(""); const [confirm, setConfirm] = useState(""); const [step, setStep] = useState(1); const [err, setErr] = useState("");
  const digit = async d => {
    if (step === 1) { const n = pin + d; setPin(n); if (n.length === 4) setTimeout(() => setStep(2), 300); }
    else { const n = confirm + d; setConfirm(n); if (n.length === 4) { if (n !== pin) { setErr("Ne poklapaju se"); setTimeout(() => { setPin(""); setConfirm(""); setStep(1); setErr(""); }, 1200); } else { const h = await hashPin(n); savePinData({ admin_pin_hash: h, sector_pins: [] }); onComplete(); } } }
  };
  const cur = step === 1 ? pin : confirm;
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: C.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <style>{`@keyframes dp{0%{transform:scale(0)}50%{transform:scale(1.3)}100%{transform:scale(1)}}`}</style>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🍳</div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: "32px 36px", textAlign: "center", maxWidth: 340 }}>
        <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Admin PIN</h2>
        <p style={{ color: C.muted, fontSize: 13, margin: "0 0 6px" }}>{step === 1 ? "Unesite 4-cifreni PIN" : "Potvrdite PIN"}</p>
        <p style={{ color: C.accent, fontSize: 12, margin: "0 0 20px" }}>{user.name}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 8 }}>{[0, 1, 2, 3].map(i => <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", border: cur.length > i ? "none" : `2px solid ${C.border}`, background: cur.length > i ? (err ? C.red : C.accent) : "transparent", transition: "all 0.15s", animation: cur.length === i + 1 ? "dp 0.2s" : "none" }} />)}</div>
        <div style={{ height: 18, marginBottom: 14 }}>{err && <span style={{ color: C.red, fontSize: 12 }}>{err}</span>}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,56px)", gap: 8, justifyContent: "center" }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <button key={n} onClick={() => digit(String(n))} style={{ width: 56, height: 56, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 20, fontWeight: 500, cursor: "pointer", fontFamily: C.font }}>{n}</button>)}
          <div /><button onClick={() => digit("0")} style={{ width: 56, height: 56, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 20, cursor: "pointer", fontFamily: C.font }}>0</button>
          <button onClick={() => { if (step === 1) setPin(p => p.slice(0, -1)); else setConfirm(p => p.slice(0, -1)); setErr(""); }} style={{ width: 56, height: 56, borderRadius: 14, border: "none", background: "transparent", color: C.muted, fontSize: 14, cursor: "pointer" }}>⌫</button>
        </div>
        <div style={{ marginTop: 12, color: C.muted, fontSize: 11 }}>Korak {step}/2</div>
      </div>
    </div>
  );
}

// Reusable toggle row for the Filteri tab. Reads/writes a single localStorage
// key (default ON when missing — matches existing kitchen install behavior).
// On change, dispatches a custom 'kitchenFilterChange' event so the running
// KitchenDisplay component re-reads its filter state instantly — no reload
// needed, no prop drilling between Admin and Display components.
function FilterToggleRow({ id, label, desc, defaultOn = true }) {
  // Default when the key has never been set: defaultOn (existing toggles keep
  // their default-ON behavior; new opt-in toggles can pass defaultOn={false}).
  const [on, setOn] = useState(() => {
    const v = localStorage.getItem(id);
    return v === null ? defaultOn : v !== "false";
  });
  const toggle = () => {
    const next = !on;
    setOn(next);
    localStorage.setItem(id, next ? "true" : "false");
    // Notify the kitchen display (running in another component tree) that
    // filter state changed. Plain dispatchEvent works same-window; the
    // 'storage' event used in some flows only fires cross-window.
    window.dispatchEvent(new Event("kitchenFilterChange"));
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${on ? C.green : C.border}`, marginBottom: 8 }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{label}</div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>{desc}</div>
      </div>
      <button
        onClick={toggle}
        style={{
          padding: "8px 16px",
          background: on ? C.green : C.card,
          color: on ? "#000" : C.muted,
          border: `1px solid ${on ? C.green : C.border}`,
          borderRadius: 7,
          cursor: "pointer",
          fontWeight: 700,
          fontSize: 12,
          fontFamily: C.font,
          minWidth: 60,
        }}
      >
        {on ? "ON" : "OFF"}
      </button>
    </div>
  );
}

// ═══ ORDER CARD — self-measuring, guarantees all products fit (no scroll) ═══
// cardMetrics() gives a good starting size from the card height + item count.
// Then we MEASURE the rendered item list: if it still overflows its fixed
// area, we shrink a scale factor proportionally and re-measure (before paint,
// via useLayoutEffect) until everything fits. This is what makes a 13- or
// 20-product order fit on one screen with no scrollbar.
function OrderCard({ o, idx, cardH, cardW, isChefView, allSectors, sector, user, onDoneItem, onDoneOrder, showOperatorName }) {
  const items = o.items || [];
  const base = cardMetrics(cardH, items.length);
  const [scale, setScale] = useState(1);
  const itemsRef = useRef(null);
  const iter = useRef(0);

  // Reset the fit whenever the content or the available area changes.
  useEffect(() => { iter.current = 0; setScale(1); }, [o.id, items.length, cardH, cardW]);

  // Measure + shrink to fit, synchronously before paint (no visible flicker).
  useLayoutEffect(() => {
    const el = itemsRef.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight + 1 && iter.current < 16) {
      const ratio = Math.max(0.3, (el.clientHeight / el.scrollHeight) * 0.96);
      iter.current += 1;
      setScale(s => Math.max(0.22, s * ratio));
    }
  });

  const mins = Math.max(0, Math.floor((Date.now() - parseTs(o.created_at)) / 60000) || 0);
  const urg = mins >= 10;
  // Apply the fit-scale to everything that affects item-row height. The button
  // now scales too (floor 11) so dense cards can actually shrink — previously a
  // 16px button floor blocked the fit and items got clipped.
  const itemFont = Math.max(7, base.itemFont * scale);
  const itemPad = Math.max(0.5, base.itemPad * scale);
  const gap = Math.max(2, base.gap * scale);
  const btn = Math.max(11, base.btn * scale);
  // Dense + wide cards flow items into 2 columns to use horizontal space.
  // Narrow cards stay single-column and rely on the measure-to-fit shrink.
  const itemCols = (items.length >= 10 && cardW >= 360) ? 2 : 1;
  const gnote = (o.note || o.notes || o.general_note || o.order_note || o.comment || o.remark || "").toString().trim();

  return (
    <div style={{ background: C.card, borderRadius: 14, border: `2px solid ${urg ? C.red : C.border}`, animation: urg ? "ub 1s infinite" : `si 0.3s ease ${Math.min(idx, 12) * 0.04}s both`, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
      {/* HEADER */}
      <div style={{ padding: `${base.headPad}px ${base.headPad + 2}px`, background: urg ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.02)", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {showOperatorName && o.source !== "self" && o.operator_name
              ? <div style={{ color: C.text, fontSize: base.headFont, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>👤 {o.operator_name}</div>
              : <div style={{ color: C.text, fontSize: base.headFont, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>#{o.order_number || o.id}</div>}
            {o.source === "self" ? (
              <span style={{ fontSize: Math.max(7, base.headFont * 0.5), fontWeight: 700, padding: "1px 5px", background: "rgba(59,130,246,0.15)", color: C.blue, border: `1px solid ${C.blue}55`, borderRadius: 4, flexShrink: 0 }}>SELF</span>
            ) : (
              <span style={{ fontSize: Math.max(7, base.headFont * 0.5), fontWeight: 700, padding: "1px 5px", background: "rgba(245,158,11,0.12)", color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 4, flexShrink: 0 }}>POS</span>
            )}
          </div>
          {o.table_name && <div style={{ color: C.accent, fontSize: Math.max(9, base.headFont * 0.7), marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📍 {o.table_name}</div>}
        </div>
        <button onClick={() => printKitchenTicket(o, isChefView ? "GLAVNI" : sector?.name, isChefView ? allSectors : null, user?.company_name)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: 3, cursor: "pointer", color: C.muted, flexShrink: 0 }}><Printer size={Math.max(11, base.headFont * 0.7)} /></button>
        <OrderTimer createdAt={o.created_at} />
      </div>
      {/* GENERAL NOTE (order-level) */}
      {gnote && (
        <div style={{ flexShrink: 0, padding: `${Math.max(3, itemPad)}px ${base.headPad + 2}px`, background: "rgba(245,158,11,0.14)", borderBottom: `1px solid ${C.accent}44`, color: C.accent, fontSize: Math.max(10, itemFont * 0.85), fontWeight: 700, lineHeight: 1.25, display: "flex", gap: 5, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0 }}>📝</span>
          <span style={{ minWidth: 0, wordBreak: "break-word" }}>{gnote}</span>
        </div>
      )}
      {/* ITEMS — measured to fit; never scrolls. Sector is inline (1 line/item). */}
      <div ref={itemsRef} style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: `2px ${base.headPad}px` }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${itemCols}, minmax(0, 1fr))`, columnGap: 10 }}>
          {items.map(it => {
            const secName = isChefView && allSectors ? (allSectors.find(s => s.id == it.sector_id)?.name || "") : "";
            return (
              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: gap, padding: `${itemPad}px 4px`, borderBottom: `1px solid ${C.border}`, opacity: it.status === "done" ? .3 : 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: itemFont, fontWeight: 700, lineHeight: 1.15, textDecoration: it.status === "done" ? "line-through" : "none", wordBreak: "break-word" }}>
                    <span style={{ color: C.accent, marginRight: 5 }}>{it.quantity}×</span>{it.product_name}
                    {secName && <span style={{ color: C.blue, fontWeight: 600, fontSize: Math.max(7, itemFont * 0.62), marginLeft: 6, whiteSpace: "nowrap" }}>[{secName}]</span>}
                  </div>
                  {(it.tags || []).length > 0 && <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>{it.tags.map((tg, i) => <span key={i} style={{ padding: "2px 8px", borderRadius: 5, background: "rgba(245,158,11,0.20)", border: `1px solid ${C.accent}`, color: "#FFFFFF", fontSize: Math.max(9, itemFont * 0.72), fontWeight: 700, lineHeight: 1.2 }}>🏷 {tg}</span>)}</div>}
                </div>
                {it.status === "pending" ? <button onClick={() => onDoneItem(it.id)} style={{ width: btn, height: btn, borderRadius: 7, border: `2px solid ${C.green}`, background: "rgba(34,197,94,0.1)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Check size={Math.max(10, btn * 0.55)} color={C.green} /></button>
                  : <div style={{ width: btn, height: btn, borderRadius: 7, background: "rgba(34,197,94,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Check size={Math.max(10, btn * 0.55)} color={C.green} /></div>}
              </div>
            );
          })}
        </div>
      </div>
      {/* FOOTER */}
      <div style={{ flexShrink: 0, padding: `${base.footPad}px ${base.headPad}px` }}>
        <button onClick={() => onDoneOrder(o.id)} style={{ width: "100%", padding: base.btnPad, background: C.green, border: "none", borderRadius: 9, color: "#000", fontSize: base.footFont, fontWeight: 700, cursor: "pointer", fontFamily: C.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><Check size={Math.max(14, base.footFont)} />GOTOVO</button>
      </div>
    </div>
  );
}

// ═══ KITCHEN DISPLAY — with print, auto-gotovo ═══
function KitchenDisplay({ sector, onLock, user, allSectors, isChefView, isSelfView }) {
  const [orders, setOrders] = useState([]); const [ld, setLd] = useState(true);
  const [sound, setSound] = useState(true); const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("kitchen_auto_print") === "true");
  // SELF Display config (only used when isSelfView=true). Loaded once on
  // mount — admin changes only apply after re-opening the view.
  const selfDisplayCfg = isSelfView ? loadSelfDisplayConfig() : null;
  // Source-based order filters. Both default to ON (show all) so existing
  // installs see no behavior change until admin explicitly disables a source
  // in Postavke → Filteri. Synced to localStorage so toggles persist across
  // app restarts.
  //   showPosOrders: orders pushed by POS fiscalize (kitchen_orders.source = 'pos' or NULL)
  //   showSelfOrders: orders pushed by SELF (kitchen_orders.source = 'self', set by SelfKitchenBridge)
  // When a source is OFF, those orders are filtered out at render time —
  // the data still arrives from the backend (we don't pass any filter
  // parameters to the API), so toggling on/off is instant with no reload.
  const [showPosOrders, setShowPosOrders] = useState(() => localStorage.getItem("kitchen_show_pos_orders") !== "false");
  const [showSelfOrders, setShowSelfOrders] = useState(() => localStorage.getItem("kitchen_show_self_orders") !== "false");
  // Header display mode: operator name instead of invoice number (default OFF —
  // unset key means OFF, opposite of the source filters above which default ON).
  const [showOperatorName, setShowOperatorName] = useState(() => localStorage.getItem("kitchen_show_operator_name") === "true");
  const prev = useRef(0); const printedRef = useRef(new Set());
  const seenRef = useRef(new Set()); const initedRef = useRef(false);
  const gridRef = useRef(null);
  const [gridSize, setGridSize] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1280, h: typeof window !== "undefined" ? window.innerHeight - 96 : 720 });
  // autoGotovo must be STABLE across renders. Reading it fresh into a plain
  // const on every render (const autoGotovo = loadAutoGotovo()) creates a NEW
  // object each render, which recreated load() every render, which restarted
  // the polling effect every render → a request flood. We hold it in a ref and
  // refresh the ref (not the identity) so load() stays stable.
  const autoGotovoRef = useRef(loadAutoGotovo());

  const load = useCallback(async () => {
    // SELF Display fetches all orders across all sectors (same as Glavni Display)
    // because it filters at render time on source='self' AND sector/product
    // exclusions. The backend doesn't know about the per-machine SELF Display
    // config, so we pull everything and filter locally.
    const u = (isChefView || isSelfView) ? "/kitchen/orders" : sector ? `/kitchen/orders?sector_id=${sector.id}` : "/kitchen/orders";
    const d = await api(u);
    if (!d.success) { setLd(false); return false; }
    {
      const o = d.data?.orders || [];
      // Detect genuinely NEW orders by id (robust: if one order completes while
      // a new one arrives, the count is unchanged but we still catch the new id).
      // On the very first load we only seed the seen-set so we don't alarm/print
      // the entire existing queue.
      const newOnes = o.filter(x => !seenRef.current.has(x.id));
      const firstLoad = !initedRef.current;
      if (!firstLoad && newOnes.length > 0) {
        if (sound) playAlert();
        if (autoPrint) {
          newOnes.forEach(order => {
            if (!printedRef.current.has(order.id)) {
              printedRef.current.add(order.id);
              printKitchenTicket(order, isChefView ? "GLAVNI KUHAR" : (isSelfView ? "SELF DISPLAY" : sector?.name), (isChefView || isSelfView) ? allSectors : null, user?.company_name);
            }
          });
        }
      }
      // Keep the seen/printed sets in sync with the current ids.
      o.forEach(order => printedRef.current.add(order.id));
      seenRef.current = new Set(o.map(x => x.id));
      initedRef.current = true;
      prev.current = o.length;
      // Auto-gotovo: check if any order exceeded the configured time
      for (const order of o) {
        const sectorId = (order.items || [])[0]?.sector_id;
        const cfg = autoGotovoRef.current[sectorId];
        if (cfg?.enabled && cfg?.minutes > 0) {
          const age = (Date.now() - parseTs(order.created_at)) / 60000;
          if (isFinite(age) && age >= cfg.minutes && order.status === "pending") {
            api(`/kitchen/doneOrder/${order.id}`, { method: "POST", body: JSON.stringify({ sector_id: sectorId }) });
          }
        }
      }
      setOrders(o);
    }
    setLd(false);
    return true;
  }, [sector, sound, autoPrint, isChefView, isSelfView, allSectors]);

  // ── Smart poller (v1.7) ────────────────────────────────────────────────────
  // Replaces the fixed setInterval. Three behaviours:
  //   1) JITTER  — each tick waits POLL + random(0..POLL_JITTER) so multiple
  //                screens/sectors never hit the server on the same instant.
  //   2) PAUSE   — when the page is hidden (TV off, PC asleep, tab switched) we
  //                stop scheduling and resume instantly on focus. Idle screens
  //                make ZERO requests.
  //   3) BACKOFF — on a failed request (network / 403 firewall burst) the delay
  //                doubles up to POLL_MAX, so a block can't turn into a retry
  //                storm. Resets to normal cadence on the next success.
  useEffect(() => {
    let timer = null;
    let stopped = false;
    let inFlight = false;   // guard: never allow two overlapping requests
    let backoff = POLL;

    const schedule = (ms) => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.hidden) return; // paused; focus handler resumes
      if (timer) { clearTimeout(timer); timer = null; }              // never stack timers
      const wait = ms + Math.floor(Math.random() * POLL_JITTER);
      timer = setTimeout(run, wait);
    };

    const run = async () => {
      if (stopped || inFlight) return;                               // don't overlap
      inFlight = true;
      let ok = false;
      try { ok = await load(); } finally { inFlight = false; }
      if (ok) backoff = POLL;                                        // healthy → normal cadence
      else backoff = Math.min(backoff * 2, POLL_MAX);                // failed → back off
      schedule(backoff);
    };

    const onVisibility = () => {
      if (stopped) return;
      if (document.hidden) {
        if (timer) { clearTimeout(timer); timer = null; }            // pause
      } else {
        if (!timer && !inFlight) { backoff = POLL; run(); }          // resume on focus
      }
    };

    // Kick off: one immediate load, then schedule the next based on the result.
    run();
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);
  // Measure the grid container so card sizes adapt to ANY screen (TV, monitor,
  // tablet) and any orientation. Updates on resize and on container changes.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const update = () => setGridSize({ w: Math.max(200, el.clientWidth - 24), h: Math.max(200, el.clientHeight - 24) });
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(update); ro.observe(el); }
    window.addEventListener("resize", update);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", update); };
  }, []);
  // Listen for filter toggle changes from other tabs / other parts of the app
  // (e.g. when admin changes the filter in Postavke). Without this, the filter
  // only applies after the component remounts. The 'storage' event fires on
  // OTHER windows; for same-window changes we use a custom 'kitchenFilterChange'
  // event dispatched from the Postavke toggle handler.
  useEffect(() => {
    const sync = () => {
      setShowPosOrders(localStorage.getItem("kitchen_show_pos_orders") !== "false");
      setShowSelfOrders(localStorage.getItem("kitchen_show_self_orders") !== "false");
      setShowOperatorName(localStorage.getItem("kitchen_show_operator_name") === "true");
    };
    window.addEventListener("storage", sync);
    window.addEventListener("kitchenFilterChange", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("kitchenFilterChange", sync);
    };
  }, []);

  const doneItem = async id => { await api(`/kitchen/doneItem/${id}`, { method: "POST" }); load(); };
  const doneOrder = async id => { await api(`/kitchen/doneOrder/${id}`, { method: "POST", body: JSON.stringify({ sector_id: sector?.id || 0 }) }); load(); };
  // Display chrome: SELF Display uses blue (matches SELF badge), Glavni uses red, sector uses sector color.
  const sc = isSelfView ? C.blue : isChefView ? "#ef4444" : sector?.color || C.accent;
  const title = isSelfView ? "SELF DISPLAY" : isChefView ? "GLAVNI DISPLAY" : sector ? sector.name.toUpperCase() : "KUHINJA";

  // Filter orders by source. order.source comes from kitchen_orders.source:
  //   'self' = pushed via SelfKitchenBridge (SELF table/terminal/kasa orders)
  //   'pos' or null or missing = pushed via POS fiscalize KitchenApiController::createOrder()
  //
  // Two filter modes:
  //   (1) SELF Display view (isSelfView=true): ONLY show source='self' orders,
  //       AND apply sector/product exclusions from SELF Display config.
  //       Global Filteri toggles are ignored here — this view is explicitly
  //       SELF-only by design.
  //   (2) Regular view: respect global Filteri toggles (showPosOrders / showSelfOrders).
  const filteredOrders = orders
    .filter(o => {
      if (isSelfView) {
        // SELF Display: only SELF-sourced orders
        if (o.source !== "self") return false;
        return true;
      }
      // Normal view: global Filteri toggles
      const isSelf = o.source === "self";
      if (isSelf && !showSelfOrders) return false;
      if (!isSelf && !showPosOrders) return false;
      return true;
    })
    .map(o => {
      // SELF Display: filter items by sector/product exclusions. If after
      // filtering all items are removed, we drop the entire order in the
      // next filter step. We mutate a shallow copy so the original orders
      // array isn't touched.
      if (!isSelfView || !selfDisplayCfg) return o;
      const exSec = new Set(selfDisplayCfg.excluded_sectors || []);
      const exProd = new Set(selfDisplayCfg.excluded_products || []);
      const items = (o.items || []).filter(it =>
        !exSec.has(Number(it.sector_id)) && !exProd.has(Number(it.product_id))
      );
      return { ...o, items };
    })
    .filter(o => !isSelfView || (o.items && o.items.length > 0)) // drop empty after exclusions
    .sort((a, b) => {
      // Newest first so brand-new orders appear at the TOP-LEFT and push older
      // ones down/right (the grid fills left-to-right, top-to-bottom).
      const ta = new Date((a.created_at || "").replace(" ", "T")).getTime() || 0;
      const tb = new Date((b.created_at || "").replace(" ", "T")).getTime() || 0;
      return tb - ta || ((b.id || 0) - (a.id || 0));
    });

  const count = filteredOrders.length;
  const GAP = 12;
  const grid = computeGrid(count, gridSize.w, gridSize.h, GAP);
  const cols = grid.cols, rows = grid.rows;
  const cardH = (gridSize.h - GAP * (rows - 1)) / rows;
  const cardW = (gridSize.w - GAP * (cols - 1)) / cols;

  return (
    <div style={{ height: "100vh", overflow: "hidden", background: C.bg, fontFamily: C.font, display: "flex", flexDirection: "column" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <style>{`@keyframes si{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}@keyframes pu{0%,100%{opacity:1}50%{opacity:.6}}@keyframes ub{0%,100%{border-color:#ef4444}50%{border-color:#fca5a5}}@keyframes sp{to{transform:rotate(360deg)}}.spin{animation:sp 1s linear infinite}`}</style>
      <div style={{ height: 60, background: C.surface, borderBottom: `3px solid ${sc}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 10, flexShrink: 0 }}>
        <div style={{ width: 10, height: 36, borderRadius: 5, background: sc }} />
        <div style={{ flex: 1, color: C.text, fontSize: 22, fontWeight: 700, letterSpacing: 2 }}>{title}</div>
        <div style={{ color: C.accent, fontSize: 32, fontWeight: 700 }}>{filteredOrders.length}</div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.2, marginRight: 4 }}>aktivnih<br />narudžbi</div>
        <button onClick={() => { const v = !autoPrint; setAutoPrint(v); localStorage.setItem("kitchen_auto_print", v ? "true" : "false"); }} title={autoPrint ? "Auto-print ON" : "Auto-print OFF"} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, cursor: "pointer", color: autoPrint ? C.accent : C.muted }}><Printer size={18} /></button>
        <button onClick={() => setSound(!sound)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, cursor: "pointer", color: sound ? C.green : C.muted }}>{sound ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
        <button onClick={load} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, cursor: "pointer", color: C.muted }}><RefreshCw size={18} /></button>
        <button onClick={onLock} style={{ background: "rgba(245,158,11,0.1)", border: `1px solid ${C.accent}44`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", color: C.accent, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, fontFamily: C.font }}><Lock size={14} />Zaključaj</button>
      </div>
      <div ref={gridRef} style={{ flex: 1, overflow: "hidden", padding: 12, minHeight: 0 }}>
        {ld ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted }}><Loader2 size={32} className="spin" /></div>
          : filteredOrders.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted }}>
              <div style={{ fontSize: 80, marginBottom: 20, opacity: .3 }}>🍳</div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>Nema narudžbi</div>
              <div style={{ fontSize: 14, marginTop: 6, animation: "pu 2s infinite" }}>● Osvježavam svake {Math.round(POLL / 1000)}s</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, gap: GAP, height: "100%", width: "100%" }}>
              {filteredOrders.map((o, idx) => (
                <OrderCard
                  key={o.id}
                  o={o}
                  idx={idx}
                  cardH={cardH}
                  cardW={cardW}
                  isChefView={isChefView}
                  allSectors={allSectors}
                  sector={sector}
                  user={user}
                  onDoneItem={doneItem}
                  onDoneOrder={doneOrder}
                  showOperatorName={showOperatorName}
                />
              ))}
            </div>
          )}
      </div>
      <div style={{ height: 36, background: C.surface, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", fontSize: 12, color: C.muted, flexShrink: 0 }}>
        <span>{user?.name} • {autoPrint ? "🖨 Auto-print ON" : ""}</span><LiveClock />
      </div>
    </div>
  );
}

// ═══ ADMIN VIEW ═══
function AdminView({ onLock, user, sectors: initSectors, onViewSector, onChefView, onSelfView }) {
  const [tab, setTab] = useState("sectors");
  const [sectors, setSectors] = useState(initSectors || []); const [products, setProducts] = useState([]); const [mappings, setMappings] = useState([]);
  const [ld, setLd] = useState(true);
  const [nn, setNn] = useState(""); const [nc, setNc] = useState("#f59e0b"); const [es, setEs] = useState(null);
  const [hist, setHist] = useState([]); const [stats, setStats] = useState(null); const [sectorStats, setSectorStats] = useState([]);
  const [toast, setToast] = useState(null);
  const [pinData, setPinData] = useState(() => loadPinData());
  const [setPinFor, setSetPinFor] = useState(null); const [sPin, setSPin] = useState(""); const [sCPin, setSCPin] = useState(""); const [sPinStep, setSPinStep] = useState(1);
  const [autoGotovo, setAutoGotovo] = useState(() => loadAutoGotovo());
  // SELF Display config — local copy for editing. Saved to localStorage on user action.
  const [selfCfg, setSelfCfg] = useState(() => loadSelfDisplayConfig());
  // SELF Display PIN modal flag. Reuses the same sPin/sCPin/sPinStep state
  // and pinDigit() handler as the sector PIN modal — proven working flow.
  const [selfPinModal, setSelfPinModal] = useState(false);
  const [printerType, setPrinterType] = useState(() => localStorage.getItem("kitchen_printer_type") || (isElectron ? "usb" : "system"));
  const [printerAddr, setPrinterAddr] = useState(() => localStorage.getItem("kitchen_printer_addr") || "");
  const [paperWidth, setPaperWidth] = useState(() => localStorage.getItem("kitchen_paper_width") || "80");
  const [systemPrinters, setSystemPrinters] = useState([]);
  const [serverStatus, setServerStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  // ═══ WinUSB direct printing state (Windows only) ═══
  // Mirrors the POS app's printer settings. When the toggle is ON, kitchen
  // tickets are sent directly to the USB thermal printer via libusb, bypassing
  // the Windows spooler. Useful for sharing the printer with other apps (like
  // Aria POS) that also use WinUSB, since both apps can take turns on the
  // device without port-switching.
  const isWindowsElectron = isElectron && /windows nt/i.test(navigator.userAgent || "");
  const [useWinUsb, setUseWinUsb] = useState(false);
  const [winUsbVid, setWinUsbVid] = useState(null);
  const [winUsbPid, setWinUsbPid] = useState(null);
  const [detectedDevice, setDetectedDevice] = useState(null);
  const [usbDevices, setUsbDevices] = useState([]);
  const [showAdvancedUsb, setShowAdvancedUsb] = useState(false);
  const [winUsbBusy, setWinUsbBusy] = useState(false);

  const show = m => { setToast(m); setTimeout(() => setToast(null), 2500); };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (isElectron && window.electronAPI?.listPrinters) {
      window.electronAPI.listPrinters().then(p => setSystemPrinters(p || []));
    }
    // Load WinUSB settings from Electron's settings file (kitchen-printer-settings.json)
    if (isElectron && window.electronAPI?.getSettings) {
      window.electronAPI.getSettings().then(s => {
        if (!s) return;
        setUseWinUsb(!!s.useWinUsb);
        setWinUsbVid(s.winUsbVid ?? null);
        setWinUsbPid(s.winUsbPid ?? null);
      });
    }
  }, []);

  // ═══ WinUSB action handlers ═══

  // Auto-detect: scan USB and find a printer-class device. Saves found VID/PID
  // to state but does NOT persist until user clicks Sačuvaj.
  const winUsbAutoDetect = async () => {
    setWinUsbBusy(true);
    setDetectedDevice(null);
    try {
      const r = await window.electronAPI.detectUsbPrinter();
      if (r.success) {
        setDetectedDevice(r.device);
        setWinUsbVid(r.device.vid);
        setWinUsbPid(r.device.pid);
        show(`Pronađen: ${r.device.product || r.device.vendor || "USB printer"}`);
      } else {
        show(r.message || "USB printer nije pronađen");
      }
    } catch (e) {
      show("Greška: " + e.message);
    }
    setWinUsbBusy(false);
  };

  // Test: verify the saved/picked VID/PID resolves to a connected device.
  const winUsbTestConnection = async () => {
    setWinUsbBusy(true);
    try {
      // Save first so the test uses the current selection
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: parseInt(paperWidth) <= 58 ? 32 : 48,
        useWinUsb: true,
        winUsbVid: winUsbVid,
        winUsbPid: winUsbPid,
        paperWidth: parseInt(paperWidth) || 80,
      });
      const r = await window.electronAPI.winUsbTest();
      show(r.success ? (r.message || "Povezan!") : (r.message || "Greška"));
    } catch (e) {
      show("Greška: " + e.message);
    }
    setWinUsbBusy(false);
  };

  // Refresh USB device list for the advanced picker
  const winUsbRefreshList = async () => {
    setWinUsbBusy(true);
    try {
      const r = await window.electronAPI.listUsbDevices();
      if (r.success) {
        setUsbDevices(r.devices || []);
        if (!r.usbAvailable) {
          show("USB modul nije dostupan u ovoj instalaciji");
        }
      } else {
        show(r.message || "Greška pri čitanju USB uređaja");
      }
    } catch (e) {
      show("Greška: " + e.message);
    }
    setWinUsbBusy(false);
  };

  const winUsbPickDevice = (dev) => {
    setWinUsbVid(dev.vid);
    setWinUsbPid(dev.pid);
    setDetectedDevice(dev);
    show(`Izabran: ${dev.product || dev.vendor || "USB device"}`);
  };
  const loadAll = async () => {
    const [s, p, m, st] = await Promise.all([api("/kitchen/sectors"), api("/kitchen/products"), api("/kitchen/mapping"), api("/kitchen/stats")]);
    if (s.success) setSectors(s.data || []);
    if (p.success) setProducts(p.data || []);
    if (m.success) setMappings(m.data || []);
    if (st.success) setStats(st.data);
    if (s.success && s.data) {
      const ss = [];
      for (const sec of s.data) { const d = await api(`/kitchen/orders?sector_id=${sec.id}`); ss.push({ ...sec, pending: d.success ? (d.data?.orders || []).length : 0 }); }
      setSectorStats(ss);
    }
    setLd(false);
  };
  const addSec = async () => { if (!nn.trim()) return; await api("/kitchen/sectors", { method: "POST", body: JSON.stringify({ name: nn.trim(), color: nc }) }); setNn(""); loadAll(); };
  const updSec = async () => { if (!es) return; await api(`/kitchen/updateSector/${es.id}`, { method: "POST", body: JSON.stringify(es) }); setEs(null); loadAll(); };
  const delSec = async id => { if (!window.confirm("Obrisati sektor?")) return; await api(`/kitchen/deleteSector/${id}`, { method: "POST" }); removeSectorPin(id); loadAll(); };
  const assign = async (pid, sid) => { if (!sid) await api("/kitchen/removeMapping", { method: "POST", body: JSON.stringify({ product_id: pid }) }); else await api("/kitchen/mapping", { method: "POST", body: JSON.stringify({ product_id: pid, sector_id: parseInt(sid) }) }); loadAll(); };
  const loadHist = async () => { const d = await api("/kitchen/history"); if (d.success) setHist(d.data?.orders || []); };

  const saveSectorPin = async () => {
    if (sPin.length !== 4 || sCPin !== sPin) { show("PIN 4 cifre"); return; }
    const h = await hashPin(sPin);
    if (h === pinData.admin_pin_hash) { show("Admin PIN!"); return; }
    const dup = (pinData.sector_pins || []).find(s => s.pin_hash === h && s.sector_id !== setPinFor.id);
    if (dup) { show("PIN zauzet: " + dup.sector_name); return; }
    const updated = { ...pinData, sector_pins: [...(pinData.sector_pins || []).filter(s => s.sector_id !== setPinFor.id), { sector_id: setPinFor.id, sector_name: setPinFor.name, pin_hash: h }] };
    savePinData(updated); setPinData(updated); setSetPinFor(null); setSPin(""); setSCPin(""); setSPinStep(1); show(`PIN za ${setPinFor.name} sačuvan`);
  };
  const removeSectorPin = (id) => { const u = { ...pinData, sector_pins: (pinData.sector_pins || []).filter(s => s.sector_id !== id) }; savePinData(u); setPinData(u); };
  const hasPin = (id) => (pinData?.sector_pins || []).find(s => s.sector_id === id);

  // ═══ SELF Display handlers ═══
  // Persist a partial update to the SELF Display config. Each handler builds
  // a full updated config object so localStorage stays in sync with the React
  // state. We also dispatch a custom event so a separately-open SELF Display
  // (in another window/tab) re-reads on next refresh.
  const persistSelfCfg = (next) => {
    setSelfCfg(next);
    saveSelfDisplayConfig(next);
  };
  const toggleSelfEnabled = () => {
    persistSelfCfg({ ...selfCfg, enabled: !selfCfg.enabled });
  };
  const toggleSelfSector = (sectorId) => {
    // Checked = INCLUDE (not in excluded list). Toggle moves the id in/out.
    const ex = new Set(selfCfg.excluded_sectors);
    if (ex.has(sectorId)) ex.delete(sectorId); else ex.add(sectorId);
    persistSelfCfg({ ...selfCfg, excluded_sectors: Array.from(ex) });
  };
  const toggleSelfProduct = (productId) => {
    const ex = new Set(selfCfg.excluded_products);
    if (ex.has(productId)) ex.delete(productId); else ex.add(productId);
    persistSelfCfg({ ...selfCfg, excluded_products: Array.from(ex) });
  };
  const removeSelfPin = () => {
    persistSelfCfg({ ...selfCfg, pin_hash: null });
    show("PIN za SELF Display uklonjen");
  };
  // Save SELF Display PIN — uses the same sPin/sCPin state as the sector PIN
  // modal, so the keypad / pinDigit handler / confirm flow are all shared.
  const saveSelfPin = async () => {
    if (sPin.length !== 4 || sCPin !== sPin) { show("PIN 4 cifre"); return; }
    const h = await hashPin(sPin);
    // Conflict check: PIN must not match admin or any sector PIN
    if (pinData?.admin_pin_hash === h) { show("Taj PIN se koristi za admin"); return; }
    if ((pinData?.sector_pins || []).find(s => s.pin_hash === h)) { show("Taj PIN se već koristi za sektor"); return; }
    persistSelfCfg({ ...selfCfg, pin_hash: h });
    setSelfPinModal(false);
    setSPin(""); setSCPin(""); setSPinStep(1);
    show("PIN za SELF Display sačuvan");
  };
  const pinDigit = d => { if (sPinStep === 1) { const n = sPin + d; setSPin(n); if (n.length === 4) setTimeout(() => setSPinStep(2), 300); } else { const n = sCPin + d; setSCPin(n); if (n.length === 4 && n !== sPin) { show("Ne poklapaju se"); setSPin(""); setSCPin(""); setSPinStep(1); } } };

  const updateAutoGotovo = (secId, field, value) => {
    const g = { ...autoGotovo }; if (!g[secId]) g[secId] = { enabled: false, minutes: 15 };
    g[secId][field] = value; setAutoGotovo(g); saveAutoGotovo(g);
  };

  const cols = ["#f59e0b", "#ef4444", "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];
  if (ld) return <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader2 size={32} color={C.accent} /></div>;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: C.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ height: 56, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 10 }}>
        <Settings size={20} color={C.accent} /><div style={{ color: C.text, fontSize: 18, fontWeight: 700, flex: 1 }}>Podešavanja kuhinje</div>
        <button onClick={onChefView} style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", color: C.red, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, fontFamily: C.font }}><Monitor size={14} />Glavni Display</button>
        {/* SELF Display button — only visible when the feature is enabled in
            the SELF Display tab. Same shape as Glavni, blue theme. */}
        {selfCfg.enabled && (
          <button onClick={onSelfView} style={{ background: "rgba(59,130,246,0.1)", border: `1px solid ${C.blue}44`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", color: C.blue, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, fontFamily: C.font }}>🍽️ SELF Display</button>
        )}
        <button onClick={onLock} style={{ background: "rgba(245,158,11,0.1)", border: `1px solid ${C.accent}44`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", color: C.accent, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, fontFamily: C.font }}><Lock size={14} />Zaključaj</button>
      </div>
      <div style={{ display: "flex", gap: 4, padding: "12px 16px 0", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        {[{ id: "sectors", l: "Sektori" }, { id: "filters", l: "🔎 Filteri" }, { id: "selfdisplay", l: "🍽️ SELF Display" }, { id: "pins", l: "🔐 PIN" }, { id: "products", l: "Proizvodi" }, { id: "printer", l: "🖨 Printer" }, { id: "autogotovo", l: "⏱ Auto-Gotovo" }, { id: "stats", l: "📊 Statistika" }, { id: "history", l: "Istorija" }].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "history") loadHist(); if (t.id === "stats") loadAll(); }} style={{ padding: "8px 14px", border: "none", borderBottom: `2px solid ${tab === t.id ? C.accent : "transparent"}`, background: "transparent", color: tab === t.id ? C.accent : C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>{t.l}</button>
        ))}
      </div>
      <div style={{ padding: 20, maxWidth: 900 }}>

        {tab === "sectors" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>Kuhinjski sektori</h3>
          {sectors.map(s => <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 8 }}>
            <div style={{ width: 12, height: 36, borderRadius: 4, background: s.color || C.accent }} /><div style={{ flex: 1 }}><div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{s.name}</div><div style={{ color: C.muted, fontSize: 12 }}>{mappings.filter(m => m.sector_id == s.id).length} proizvoda{hasPin(s.id) ? " • PIN ✓" : ""}</div></div>
            <button onClick={() => onViewSector(s)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: C.green, fontSize: 12, fontFamily: C.font, display: "flex", alignItems: "center", gap: 4 }}><Eye size={13} />Live</button>
            <button onClick={() => setEs({ ...s })} style={{ background: "none", border: "none", cursor: "pointer", color: C.blue }}><Edit3 size={16} /></button>
            <button onClick={() => delSec(s.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red }}><Trash2 size={16} /></button>
          </div>)}
          {es && <div style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginBottom: 16 }}>
            <input value={es.name} onChange={e => setEs(p => ({ ...p, name: e.target.value }))} style={{ ...inp, marginBottom: 10 }} />
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>{cols.map(c => <button key={c} onClick={() => setEs(p => ({ ...p, color: c }))} style={{ width: 30, height: 30, borderRadius: 8, background: c, border: es.color === c ? "3px solid white" : "none", cursor: "pointer" }} />)}</div>
            <div style={{ display: "flex", gap: 8 }}><button onClick={() => setEs(null)} style={{ padding: "8px 16px", background: C.card, border: "none", borderRadius: 8, color: C.muted, cursor: "pointer", fontFamily: C.font }}>Otkaži</button><button onClick={updSec} style={{ padding: "8px 16px", background: C.accent, border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontFamily: C.font }}>Sačuvaj</button></div>
          </div>}
          <div style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginTop: 16 }}>
            <h4 style={{ color: C.text, margin: "0 0 10px" }}>Novi sektor</h4>
            <input value={nn} onChange={e => setNn(e.target.value)} onKeyDown={e => e.key === "Enter" && addSec()} placeholder="Naziv (Grill, Pizza...)" style={{ ...inp, marginBottom: 10 }} />
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>{cols.map(c => <button key={c} onClick={() => setNc(c)} style={{ width: 30, height: 30, borderRadius: 8, background: c, border: nc === c ? "3px solid white" : "none", cursor: "pointer" }} />)}</div>
            <button onClick={addSec} style={{ padding: "8px 16px", background: C.accent, border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontFamily: C.font }}>+ Dodaj</button>
          </div>
        </div>}

        {tab === "filters" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Filteri narudžbi po izvoru</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>Kontrola koje narudžbe se prikazuju na kuhinjskom displeju. Korisno kada koristite obje funkcije (Fiskalizuj i Pošalji na SELF) i ne želite duplikate.</p>
          {/* POS fiscalize toggle */}
          <FilterToggleRow
            id="kitchen_show_pos_orders"
            label="Narudžbe iz POS-a (Fiskalizuj)"
            desc="Kada se račun fiskalizuje u POS-u, kuhinjski proizvodi automatski idu na kuhinju."
          />
          {/* SELF source toggle */}
          <FilterToggleRow
            id="kitchen_show_self_orders"
            label="Narudžbe iz POS-a (Pošalji na SELF)"
            desc="Kada operater u POS-u klikne 'Pošalji na SELF', narudžba ide na SELF i automatski na kuhinju."
          />
          {/* Operator name instead of invoice number (default OFF — invoice
              number is the default header, admin opts in per machine). Applies
              only to POS orders; SELF orders have no operator and always show
              the order number. */}
          <FilterToggleRow
            id="kitchen_show_operator_name"
            label="Prikaži ime operatera umjesto broja"
            desc="Na kartici narudžbe iz POS-a prikazuje ime operatera koji je poslao narudžbu umjesto broja računa. SELF narudžbe uvijek prikazuju broj."
            defaultOn={false}
          />
          <div style={{ marginTop: 14, padding: 12, background: "rgba(245,158,11,0.08)", border: `1px solid ${C.accent}33`, borderRadius: 8, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            <b style={{ color: C.accent }}>Savjet:</b> Ako operater u POS-u <i>prvo</i> šalje stavke na SELF, a <i>zatim</i> fiskalizuje cijeli račun, ista hrana može stići na kuhinju <b>dva puta</b>. U tom slučaju isključite jedan od ova dva izvora — obično se isključuje "Narudžbe iz POS-a (Fiskalizuj)" jer SELF već automatski šalje na kuhinju.
          </div>
        </div>}

        {/* ═══ SELF DISPLAY TAB ═══
            Configures the dedicated SELF Display view (a kitchen view showing
            only source='self' orders across all sectors, similar to Glavni
            Display but for SELF orders only). Config is per-machine (localStorage). */}
        {tab === "selfdisplay" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>SELF Display</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>Poseban kuhinjski prikaz koji pokazuje <b>samo narudžbe iz SELF aplikacije</b> (sa svih sektora). Korisno za posebnu stanicu koja obrađuje samo SELF narudžbe.</p>

          {/* Master toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${selfCfg.enabled ? C.blue : C.border}`, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Aktiviraj SELF Display</div>
              <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Kada je uključeno, novi PIN ekran ima opciju za otvaranje SELF Display-a.</div>
            </div>
            <button
              onClick={toggleSelfEnabled}
              style={{ padding: "8px 16px", background: selfCfg.enabled ? C.blue : C.card, color: selfCfg.enabled ? "#fff" : C.muted, border: `1px solid ${selfCfg.enabled ? C.blue : C.border}`, borderRadius: 7, cursor: "pointer", fontWeight: 700, fontSize: 12, fontFamily: C.font, minWidth: 60 }}
            >{selfCfg.enabled ? "ON" : "OFF"}</button>
          </div>

          {selfCfg.enabled && <>
            {/* PIN management — opens the same modal style as sector PINs */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>PIN za SELF Display</div>
                <div style={{ color: selfCfg.pin_hash ? C.green : C.muted, fontSize: 12, marginTop: 2 }}>{selfCfg.pin_hash ? "✓ PIN postavljen" : "Nema PIN-a — postavite ga za pristup"}</div>
              </div>
              <button onClick={() => { setSelfPinModal(true); setSPin(""); setSCPin(""); setSPinStep(1); }} style={{ padding: "8px 16px", background: selfCfg.pin_hash ? C.card : C.blue, border: `1px solid ${selfCfg.pin_hash ? C.border : C.blue}`, borderRadius: 8, color: selfCfg.pin_hash ? C.text : "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: C.font }}>{selfCfg.pin_hash ? "Promijeni" : "Postavi PIN"}</button>
              {selfCfg.pin_hash && <button onClick={removeSelfPin} style={{ padding: "8px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.red, cursor: "pointer" }}><Trash2 size={14} /></button>}
            </div>

            {/* Sector exclusion list */}
            <div style={{ padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Sektori uključeni u SELF Display</div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Označite sektore čije narudžbe žeite prikazati. Neoznačeni sektori se neće prikazati.</div>
              {sectors.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>Nema definisanih sektora.</div> : sectors.map(s => {
                const included = !selfCfg.excluded_sectors.includes(s.id);
                return (
                  <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: included ? "rgba(34,197,94,0.06)" : C.card, border: `1px solid ${included ? `${C.green}44` : C.border}`, borderRadius: 8, marginBottom: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={included} onChange={() => toggleSelfSector(s.id)} style={{ width: 16, height: 16, accentColor: C.blue, cursor: "pointer" }} />
                    <div style={{ width: 10, height: 22, borderRadius: 3, background: s.color || C.accent }} />
                    <div style={{ flex: 1, color: C.text, fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                  </label>
                );
              })}
            </div>

            {/* Product exclusion list */}
            <div style={{ padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Proizvodi uključeni u SELF Display</div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Označite proizvode koje žeite prikazati. Neoznačeni se neće prikazati čak i ako stignu iz SELF-a.</div>
              {products.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: 8 }}>Nema kuhinjskih proizvoda.</div> : (
                <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                  {products.map(p => {
                    const included = !selfCfg.excluded_products.includes(p.id);
                    const m = mappings.find(mm => mm.product_id == p.id);
                    const sec = m ? sectors.find(s => s.id == m.sector_id) : null;
                    return (
                      <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: included ? "rgba(34,197,94,0.04)" : C.card, border: `1px solid ${included ? `${C.green}33` : C.border}`, borderRadius: 6, marginBottom: 4, cursor: "pointer" }}>
                        <input type="checkbox" checked={included} onChange={() => toggleSelfProduct(p.id)} style={{ width: 14, height: 14, accentColor: C.blue, cursor: "pointer" }} />
                        <div style={{ flex: 1, color: C.text, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                        {sec && <span style={{ fontSize: 10, padding: "2px 6px", background: `${sec.color}22`, color: sec.color, borderRadius: 4, flexShrink: 0 }}>{sec.name}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </>}

          {/* SELF Display PIN modal — same UX as sector PIN modal (lines below)
              but uses saveSelfPin handler and blue accent color. Shares state
              with sector PIN modal: sPin, sCPin, sPinStep, pinDigit(). */}
          {selfPinModal && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setSelfPinModal(false); setSPin(""); setSCPin(""); setSPinStep(1); }}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 16, padding: 28, width: 380, border: `1px solid ${C.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 36, margin: "0 0 6px" }}>🍽️</div>
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>SELF Display</h3>
              <p style={{ color: C.muted, fontSize: 13, margin: "0 0 20px" }}>{sPinStep === 1 ? "Unesite 4-cifreni PIN" : "Potvrdite PIN"}</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 12 }}>{[0, 1, 2, 3].map(i => { const v = sPinStep === 1 ? sPin : sCPin; return <div key={i} style={{ width: 40, height: 40, borderRadius: 8, border: `2px solid ${v.length > i ? C.blue : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: C.text }}>{v.length > i ? "•" : ""}</div>; })}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4, maxWidth: 220, margin: "0 auto 16px" }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <button key={n} onClick={() => pinDigit(String(n))} style={{ height: 44, borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>{n}</button>)}
                <div /><button onClick={() => pinDigit("0")} style={{ height: 44, borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>0</button>
                <button onClick={() => { if (sPinStep === 1) setSPin(p => p.slice(0, -1)); else setSCPin(p => p.slice(0, -1)); }} style={{ height: 44, borderRadius: 6, border: "none", background: "rgba(239,68,68,0.1)", color: C.red, fontSize: 13, cursor: "pointer" }}>⌫</button>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setSelfPinModal(false); setSPin(""); setSCPin(""); setSPinStep(1); }} style={{ flex: 1, padding: 12, background: C.card, border: "none", borderRadius: 8, color: C.muted, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>Otkaži</button>
                <button onClick={saveSelfPin} disabled={sPin.length !== 4 || sCPin !== sPin} style={{ flex: 1, padding: 12, background: C.blue, border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: C.font, opacity: (sPin.length === 4 && sCPin === sPin) ? 1 : 0.4 }}>Sačuvaj</button>
              </div>
            </div>
          </div>}
        </div>}

        {tab === "pins" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>PIN kodovi za sektore</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>Svaki sektor ima svoj PIN. Na PIN ekranu unesite PIN → otvara se live feed.</p>
          {sectors.map(s => { const hp = hasPin(s.id); return <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 8 }}>
            <div style={{ width: 10, height: 32, borderRadius: 4, background: s.color || C.accent }} /><div style={{ flex: 1 }}><div style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>{s.name}</div><div style={{ color: hp ? C.green : C.muted, fontSize: 12 }}>{hp ? "✓ PIN postavljen" : "Nema PIN-a"}</div></div>
            <button onClick={() => { setSetPinFor(s); setSPin(""); setSCPin(""); setSPinStep(1); }} style={{ padding: "8px 16px", background: hp ? C.card : C.accent, border: `1px solid ${hp ? C.border : C.accent}`, borderRadius: 8, color: hp ? C.text : "#000", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: C.font }}>{hp ? "Promijeni" : "Postavi PIN"}</button>
            {hp && <button onClick={() => { removeSectorPin(s.id); show("PIN uklonjen"); }} style={{ padding: "8px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.red, cursor: "pointer" }}><Trash2 size={14} /></button>}
          </div>; })}
          {setPinFor && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSetPinFor(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 16, padding: 28, width: 380, border: `1px solid ${C.border}`, textAlign: "center" }}>
              <div style={{ width: 10, height: 40, borderRadius: 4, background: setPinFor.color || C.accent, margin: "0 auto 10px" }} />
              <h3 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{setPinFor.name}</h3>
              <p style={{ color: C.muted, fontSize: 13, margin: "0 0 20px" }}>{sPinStep === 1 ? "Unesite 4-cifreni PIN" : "Potvrdite PIN"}</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 12 }}>{[0, 1, 2, 3].map(i => { const v = sPinStep === 1 ? sPin : sCPin; return <div key={i} style={{ width: 40, height: 40, borderRadius: 8, border: `2px solid ${v.length > i ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: C.text }}>{v.length > i ? "•" : ""}</div>; })}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4, maxWidth: 220, margin: "0 auto 16px" }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <button key={n} onClick={() => pinDigit(String(n))} style={{ height: 44, borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>{n}</button>)}
                <div /><button onClick={() => pinDigit("0")} style={{ height: 44, borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>0</button>
                <button onClick={() => { if (sPinStep === 1) setSPin(p => p.slice(0, -1)); else setSCPin(p => p.slice(0, -1)); }} style={{ height: 44, borderRadius: 6, border: "none", background: "rgba(239,68,68,0.1)", color: C.red, fontSize: 13, cursor: "pointer" }}>⌫</button>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setSetPinFor(null)} style={{ flex: 1, padding: 12, background: C.card, border: "none", borderRadius: 8, color: C.muted, fontWeight: 600, cursor: "pointer", fontFamily: C.font }}>Otkaži</button>
                <button onClick={saveSectorPin} disabled={sPin.length !== 4 || sCPin !== sPin} style={{ flex: 1, padding: 12, background: C.accent, border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontFamily: C.font, opacity: (sPin.length === 4 && sCPin === sPin) ? 1 : 0.4 }}>Sačuvaj</button>
              </div>
            </div>
          </div>}
        </div>}

        {tab === "products" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Raspodjela proizvoda po sektorima</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>Dodijelite proizvod sektoru</p>
          {products.length === 0 ? <p style={{ color: C.muted }}>Uključite proizvode u POS → Podešavanja → 🍳 Kuhinja</p> : products.map(p => {
            const cur = mappings.find(m => m.product_id == p.id);
            return <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 6 }}>
              <Package size={16} color={C.muted} /><div style={{ flex: 1, color: C.text, fontSize: 14, fontWeight: 600 }}>{p.name}</div>
              <select value={cur?.sector_id || ""} onChange={e => assign(p.id, e.target.value)} style={{ ...inp, width: "auto", minWidth: 150, padding: "8px 12px" }}><option value="">— Nema —</option>{sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            </div>;
          })}
        </div>}

        {tab === "printer" && <div style={{ maxWidth: 560 }}>
          <div style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 22, marginBottom: 16 }}>
            <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: "0 0 16px" }}>🖨 Termalni printer</h3>
            <div style={{ padding: "8px 14px", background: "rgba(34,197,94,0.1)", borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.green, display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle size={16} />
              {isElectron ? "Windows desktop — direktno ESC/POS štampanje" : "Web pretraživač — sistemski dijalog za štampu"}
            </div>

            {isElectron ? <>
              <label style={{ color: C.text, fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Printer adresa / interfejs</label>
              <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="tcp://192.168.1.100:9100 ili USB printer ime" style={{ ...inp, marginBottom: 8 }} />
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
                Mrežni: tcp://IP:9100 · USB (Windows): \\localhost\ImeStampaca
              </div>
              {systemPrinters.length > 0 && <>
                <label style={{ color: C.text, fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Ili izaberite sistemski printer</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                  {systemPrinters.map(p => (
                    <button key={p.name} onClick={() => setPrinterAddr(p.name)} style={{ padding: "10px 14px", borderRadius: 8, border: `2px solid ${printerAddr === p.name ? C.green : C.border}`, background: printerAddr === p.name ? "rgba(34,197,94,0.1)" : C.card, textAlign: "left", cursor: "pointer", fontFamily: C.font, fontSize: 13, color: C.text }}>
                      <span style={{ fontWeight: 600 }}>{p.name}</span>
                      {p.isDefault && <span style={{ marginLeft: 8, fontSize: 11, color: C.green }}>(default)</span>}
                    </button>
                  ))}
                </div>
              </>}
            </> : <>
              <label style={{ color: C.text, fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Način štampanja</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={() => { setPrinterType("system"); localStorage.setItem("kitchen_printer_type", "system"); }} style={{ flex: 1, padding: 12, borderRadius: 8, border: `2px solid ${printerType === "system" ? C.green : C.border}`, background: printerType === "system" ? "rgba(34,197,94,0.1)" : C.card, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: C.font, color: printerType === "system" ? C.green : C.muted }}>Sistemski dijalog</button>
                <button onClick={() => { setPrinterType("network"); localStorage.setItem("kitchen_printer_type", "network"); }} style={{ flex: 1, padding: 12, borderRadius: 8, border: `2px solid ${printerType === "network" ? C.green : C.border}`, background: printerType === "network" ? "rgba(34,197,94,0.1)" : C.card, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: C.font, color: printerType === "network" ? C.green : C.muted }}>Mrežni printer</button>
              </div>
              {printerType === "network" && <>
                <label style={{ color: C.text, fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Print server URL</label>
                <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="http://192.168.1.100:9100" style={{ ...inp, marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>URL vašeg print servera ili TCP adresa printera</div>
              </>}
              {printerType === "system" && <div style={{ padding: 14, background: "rgba(59,130,246,0.1)", borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.blue }}>Koristi sistemski dijalog — izaberite printer kad kliknete "Štampaj".</div>}
            </>}

            <label style={{ color: C.text, fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Širina papira</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {["57", "58", "76", "80"].map(w => (
                <button key={w} onClick={() => setPaperWidth(w)} style={{ padding: "10px 20px", borderRadius: 8, border: `2px solid ${paperWidth === w ? C.green : C.border}`, background: paperWidth === w ? "rgba(34,197,94,0.1)" : C.card, fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: C.font, color: paperWidth === w ? C.green : C.muted }}>{w}mm</button>
              ))}
            </div>

            {/* ═══ WinUSB direct printing (Windows desktop only) ═══
                When toggle is ON, all print jobs go directly to the USB printer
                via libusb — no Windows printer driver needed. Useful for sharing
                the printer with other apps (like Aria POS) without port-switching. */}
            {isWindowsElectron && (
              <div style={{ marginBottom: 20, padding: 16, background: useWinUsb ? "rgba(34,197,94,0.06)" : C.card, border: `1px solid ${useWinUsb ? C.green : C.border}`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: useWinUsb ? 14 : 4 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>WinUSB direktno štampanje</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      Šalje ESC/POS direktno preko USB-a, bez Windows drajvera za printer.
                    </div>
                  </div>
                  <button
                    onClick={() => setUseWinUsb(v => !v)}
                    style={{
                      padding: "8px 14px",
                      background: useWinUsb ? C.green : C.card,
                      color: useWinUsb ? "#000" : C.muted,
                      border: `1px solid ${useWinUsb ? C.green : C.border}`,
                      borderRadius: 7,
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontFamily: C.font,
                    }}
                  >
                    {useWinUsb ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    {useWinUsb ? "ON" : "OFF"}
                  </button>
                </div>

                {useWinUsb && (
                  <>
                    {/* Status / detected device info */}
                    {(detectedDevice || (winUsbVid && winUsbPid)) ? (
                      <div style={{ padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Aktivni printer:</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                          {detectedDevice?.product || detectedDevice?.vendor || "USB Printer"}
                        </div>
                        <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
                          VID:{(winUsbVid ?? 0).toString(16).padStart(4, "0").toUpperCase()} PID:{(winUsbPid ?? 0).toString(16).padStart(4, "0").toUpperCase()}
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: "10px 12px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, marginBottom: 10, fontSize: 12, color: "#fbbf24" }}>
                        Printer još nije izabran. Kliknite "Auto-detekcija" ili izaberite ručno u Naprednim postavkama.
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                      <button onClick={winUsbAutoDetect} disabled={winUsbBusy} style={{ flex: "1 1 calc(50% - 3px)", padding: "8px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: C.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: winUsbBusy ? 0.5 : 1 }}>
                        {winUsbBusy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
                        Auto-detekcija
                      </button>
                      <button onClick={winUsbTestConnection} disabled={winUsbBusy || !winUsbVid} style={{ flex: "1 1 calc(50% - 3px)", padding: "8px 10px", borderRadius: 7, border: "none", background: C.green, color: "#000", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: C.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (winUsbBusy || !winUsbVid) ? 0.5 : 1 }}>
                        <CheckCircle size={13} />
                        Testiraj
                      </button>
                    </div>

                    <button
                      onClick={() => { const next = !showAdvancedUsb; setShowAdvancedUsb(next); if (next && usbDevices.length === 0) winUsbRefreshList(); }}
                      style={{ width: "100%", padding: "8px 12px", background: "transparent", border: `1px dashed ${C.border}`, borderRadius: 7, cursor: "pointer", fontSize: 12, color: C.muted, fontWeight: 600, fontFamily: C.font }}
                    >
                      {showAdvancedUsb ? "▾" : "▸"} Napredno (ručni izbor uređaja)
                    </button>

                    {showAdvancedUsb && (
                      <div style={{ marginTop: 10, padding: 10, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 12, color: C.muted }}>Svi USB uređaji ({usbDevices.length})</div>
                          <button onClick={winUsbRefreshList} disabled={winUsbBusy} style={{ padding: "4px 8px", background: C.card, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: C.font, display: "flex", alignItems: "center", gap: 4 }}><RefreshCw size={11} />Osvježi</button>
                        </div>
                        {usbDevices.length === 0 && !winUsbBusy && (
                          <div style={{ fontSize: 12, color: C.muted, padding: 12, textAlign: "center" }}>
                            Nema dostupnih USB uređaja. Provjerite drajver (WinUSB / libusb).
                          </div>
                        )}
                        {usbDevices.map((d, i) => (
                          <button
                            key={`${d.vid}-${d.pid}-${i}`}
                            onClick={() => winUsbPickDevice(d)}
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              background: (winUsbVid === d.vid && winUsbPid === d.pid) ? "rgba(34,197,94,0.1)" : C.card,
                              border: `1px solid ${(winUsbVid === d.vid && winUsbPid === d.pid) ? C.green : C.border}`,
                              borderRadius: 6,
                              marginBottom: 4,
                              cursor: "pointer",
                              textAlign: "left",
                              fontFamily: C.font,
                              fontSize: 12,
                              color: C.text,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {d.isPrinter && <span title="Printer-class device" style={{ fontSize: 11 }}>🖨️</span>}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {d.product || d.vendor || "Unknown device"}
                              </div>
                              <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
                                VID:{d.vidHex.toUpperCase()} PID:{d.pidHex.toUpperCase()}
                                {d.isPrinter && <span style={{ marginLeft: 6, color: C.green }}>· printer-class</span>}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <div style={{ marginTop: 10, fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
                      ⚠️ Za WinUSB potreban je libusb drajver. Ako auto-detekcija ne nalazi printer, instalirajte WinUSB drajver pomoću alata <b>Zadig</b> (zadig.akeo.ie) i pokušajte ponovo.
                    </div>
                  </>
                )}
              </div>
            )}

            <button onClick={() => {
              localStorage.setItem("kitchen_printer_type", printerType);
              localStorage.setItem("kitchen_printer_addr", printerAddr);
              localStorage.setItem("kitchen_paper_width", paperWidth);
              if (isElectron && window.electronAPI) {
                window.electronAPI.saveSettings({
                  type: "epson",
                  interface: printerAddr,
                  printerName: printerAddr,
                  width: parseInt(paperWidth) <= 58 ? 32 : 48,
                  // Persist WinUSB fields too — main.js reads these on each
                  // printText call to decide between WinUSB and spooler path.
                  useWinUsb: !!useWinUsb,
                  winUsbVid: winUsbVid,
                  winUsbPid: winUsbPid,
                  paperWidth: parseInt(paperWidth) || 80,
                });
              }
              show("Postavke printera sačuvane!");
            }} style={{ width: "100%", padding: 14, background: C.accent, border: "none", borderRadius: 10, color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: C.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Save size={16} />Sačuvaj postavke</button>
          </div>

          <div style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 22 }}>
            <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Testiranje</h3>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <button onClick={async () => {
                setTesting(true); setServerStatus(null);
                if (isElectron && window.electronAPI) { const r = await window.electronAPI.testConnection(); setServerStatus({ ok: r.success, msg: r.message || (r.success ? "Povezan!" : "Greška") }); }
                else { setServerStatus({ ok: true, msg: printerType === "network" ? "Mrežni printer konfigurisan" : "Sistemski dijalog za štampu dostupan" }); }
                setTesting(false);
              }} disabled={testing} style={{ flex: 1, padding: 12, borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontWeight: 600, cursor: "pointer", fontFamily: C.font, fontSize: 13 }}>{testing ? "..." : "Provjeri konekciju"}</button>
              <button onClick={async () => {
                setTesting(true);
                const testOrder = { id: 0, order_number: "TEST", table_name: "Test Sto", items: [{ id: 1, quantity: 2, product_name: "Test Proizvod", tags: ["test tag"], status: "pending" }] };
                await printKitchenTicket(testOrder, "TEST KUHINJA", null, user?.company_name);
                show("Test štampa poslata!");
                setTesting(false);
              }} disabled={testing} style={{ flex: 1, padding: 12, borderRadius: 8, background: C.green, border: "none", color: "#000", fontWeight: 700, cursor: "pointer", fontFamily: C.font, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Printer size={15} />Test print</button>
            </div>
            {serverStatus && (
              <div style={{ padding: 14, borderRadius: 8, background: serverStatus.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${serverStatus.ok ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                {serverStatus.ok ? <CheckCircle size={18} color={C.green} /> : <XCircle size={18} color={C.red} />}
                <span style={{ color: serverStatus.ok ? C.green : C.red }}>{serverStatus.msg}</span>
              </div>
            )}
          </div>
        </div>}

        {tab === "autogotovo" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>⏱ Auto-Gotovo tajmer</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>Narudžba se automatski označava kao gotova nakon zadatog vremena. Ako je isključeno, samo ručno dugme GOTOVO.</p>
          {sectors.map(s => {
            const cfg = autoGotovo[s.id] || { enabled: false, minutes: 15 };
            return <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 10 }}>
              <div style={{ width: 10, height: 40, borderRadius: 4, background: s.color || C.accent }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{s.name}</div>
                <div style={{ color: cfg.enabled ? C.green : C.muted, fontSize: 12, marginTop: 2 }}>{cfg.enabled ? `Auto-gotovo: ${cfg.minutes} min` : "Ručno (GOTOVO dugme)"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {cfg.enabled && <input type="number" value={cfg.minutes} onChange={e => updateAutoGotovo(s.id, "minutes", Math.max(1, parseInt(e.target.value) || 1))} style={{ ...inp, width: 70, padding: "6px 8px", textAlign: "center" }} min="1" />}
                {cfg.enabled && <span style={{ color: C.muted, fontSize: 12 }}>min</span>}
                <button onClick={() => updateAutoGotovo(s.id, "enabled", !cfg.enabled)} style={{ width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: cfg.enabled ? C.green : "#555", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: "white", position: "absolute", top: 3, left: cfg.enabled ? 25 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </button>
              </div>
            </div>;
          })}
        </div>}

        {tab === "stats" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>📊 Statistika</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
            {[{ v: stats?.pending_orders || 0, l: "Na čekanju", c: C.accent }, { v: stats?.done_today || 0, l: "Završeno danas", c: C.green }, { v: stats?.avg_completion_seconds ? Math.floor(stats.avg_completion_seconds / 60) + "m" : "—", l: "Prosječno", c: C.blue }].map((s, i) =>
              <div key={i} style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, textAlign: "center" }}><div style={{ color: s.c, fontSize: 36, fontWeight: 700 }}>{s.v}</div><div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{s.l}</div></div>
            )}
          </div>
          <h4 style={{ color: C.text, fontSize: 15, fontWeight: 700, margin: "0 0 12px" }}>Po sektorima</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {sectorStats.map(s => (
              <div key={s.id} style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 8, height: 48, borderRadius: 4, background: s.color || C.accent }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>{s.name}</div>
                  <div style={{ color: s.pending > 0 ? C.accent : C.green, fontSize: 13, marginTop: 2 }}>{s.pending > 0 ? `⏳ ${s.pending} na čekanju` : "✓ Nema"}</div>
                </div>
                <button onClick={() => onViewSector(s)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: C.green, fontSize: 12, fontFamily: C.font }}><Eye size={13} /></button>
              </div>
            ))}
          </div>
        </div>}

        {tab === "history" && <div>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>Istorija (12h)</h3>
          {hist.length === 0 ? <p style={{ color: C.muted }}>Nema završenih</p> : hist.map(o => <div key={o.id} style={{ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, padding: "12px 16px", marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}><Check size={14} color={C.green} /><span style={{ color: C.text, fontWeight: 700 }}>#{o.order_number || o.id}</span>{o.table_name && <span style={{ color: C.accent, fontSize: 13 }}>📍 {o.table_name}</span>}<span style={{ flex: 1 }} /><span style={{ color: C.muted, fontSize: 12 }}>{o.done_at ? new Date(o.done_at).toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" }) : ""}</span></div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{(o.items || []).map(it => <span key={it.id} style={{ padding: "3px 8px", borderRadius: 5, background: C.card, color: C.muted, fontSize: 12 }}>{it.quantity}× {it.product_name}</span>)}</div>
          </div>)}
        </div>}
      </div>
      {toast && <div style={{ position: "fixed", bottom: 24, right: 24, padding: "12px 20px", borderRadius: 10, background: C.green, color: "#000", fontSize: 14, fontWeight: 700, zIndex: 9999, fontFamily: C.font }}>{toast}</div>}
    </div>
  );
}

// ═══ MAIN APP ═══
export default function App() {
  const [auth, setAuth] = useState(() => {
    try { const t = localStorage.getItem("kitchen_token"); const u = localStorage.getItem("kitchen_user"); if (t && u && u !== "undefined") return { token: t, user: JSON.parse(u) }; } catch { localStorage.removeItem("kitchen_token"); localStorage.removeItem("kitchen_user"); } return null;
  });
  const [pinState, setPinState] = useState("checking");
  const [unlockResult, setUnlockResult] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [viewSector, setViewSector] = useState(null);
  const [chefView, setChefView] = useState(false);
  // SELF Display view state. Set true either by admin clicking the SELF Display
  // button in AdminView, or by user entering the SELF Display PIN on the PIN screen.
  const [selfView, setSelfView] = useState(false);

  useEffect(() => {
    if (!auth) { setPinState("checking"); return; }
    const pd = loadPinData();
    if (!pd || !pd.admin_pin_hash) setPinState("setup");
    else setPinState("locked");
    api("/kitchen/sectors").then(d => { if (d.success) setSectors(d.data || []); });
  }, [auth]);

  const login = (t, u) => { localStorage.setItem("kitchen_token", t); localStorage.setItem("kitchen_user", JSON.stringify(u)); setAuth({ token: t, user: u }); };
  const lock = () => { setUnlockResult(null); setViewSector(null); setChefView(false); setSelfView(false); setPinState("locked"); api("/kitchen/sectors").then(d => { if (d.success) setSectors(d.data || []); }); };

  if (!auth) return <LoginScreen onLogin={login} />;
  if (pinState === "setup") return <PinSetup user={auth.user} onComplete={() => setPinState("locked")} />;
  if (pinState === "locked") { const pd = loadPinData(); return <PinScreen onUnlock={r => { setUnlockResult(r); setPinState("unlocked"); if (r.type === "self_display") setSelfView(true); }} pinData={pd} companyName={auth.user.company_name} />; }
  if (pinState === "checking") return <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader2 size={32} color={C.accent} /></div>;

  // SELF Display view — only source='self' orders, with per-machine config
  if (selfView) return <KitchenDisplay sector={null} onLock={() => { setSelfView(false); if (unlockResult?.type === "self_display") lock(); }} user={auth.user} allSectors={sectors} isSelfView={true} />;

  // Chef view — all orders, all sectors labeled
  if (chefView) return <KitchenDisplay sector={null} onLock={() => setChefView(false)} user={auth.user} allSectors={sectors} isChefView={true} />;

  // Admin viewing a specific sector
  if (viewSector) return <KitchenDisplay sector={viewSector} onLock={() => setViewSector(null)} user={auth.user} />;

  // Admin panel
  if (unlockResult?.type === "admin") return <AdminView onLock={lock} user={auth.user} sectors={sectors} onViewSector={s => setViewSector(s)} onChefView={() => setChefView(true)} onSelfView={() => setSelfView(true)} />;

  // Sector PIN
  if (unlockResult?.type === "sector") {
    const sec = sectors.find(s => s.id == unlockResult.sector_id) || { id: unlockResult.sector_id, name: unlockResult.sector_name, color: C.accent };
    return <KitchenDisplay sector={sec} onLock={lock} user={auth.user} />;
  }

  return <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading...</div>;
}
